import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Breadcrumbs,
  Button,
  Paper,
  Divider,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  Wrench,
  ArrowLeft,
  Clock,
  BriefcaseBusiness,
  Rocket,
  Download,
  CheckCircle,
} from "lucide-react";
import PageShell from "../components/layout/PageShell";
import { getBusinessOffering } from "../api/business";

export default function OfferingDetailPage() {
  const { slug } = useParams();

  const { data: offering, isLoading } = useQuery({
    queryKey: ["offering-detail", slug],
    queryFn: () => getBusinessOffering(slug).then((r) => r.data),
    enabled: !!slug,
  });

  const blue = "#2458D3";
  const navy = "#071B3A";

  const details = [
    {
      label: "Service type",
      value: offering?.category?.name || "Professional Services",
      icon: BriefcaseBusiness,
    },
    {
      label: "Service",
      value: offering?.title || "Business Service",
      icon: Wrench,
    },
    {
      label: "Availability",
      value: "Contact us",
      icon: Clock,
    },
  ];

  return (
    <PageShell>
      <Box sx={{ bgcolor: "#F5F8FC", minHeight: "100vh", pb: 8 }}>
        {/* Premium blue header */}
        <Box
          sx={{
            background:
              "linear-gradient(120deg, #071B3A 0%, #123D83 55%, #2458D3 100%)",
            color: "#FFFFFF",
            pt: { xs: 3, md: 4 },
            pb: { xs: 10, md: 12 },
          }}
        >
          <Container maxWidth="lg">
            <Breadcrumbs
              separator="/"
              sx={{
                mb: 3,
                "& .MuiBreadcrumbs-separator": {
                  color: "rgba(255,255,255,0.6)",
                },
              }}
            >
              <Typography
                component={Link}
                to="/"
                sx={{
                  color: "#D1DDF2",
                  fontSize: 13,
                  textDecoration: "none",
                }}
              >
                Home
              </Typography>

              <Typography
                component={Link}
                to="/business?tab=it-services"
                sx={{
                  color: "#D1DDF2",
                  fontSize: 13,
                  textDecoration: "none",
                }}
              >
                IT Services & Consultancy
              </Typography>

              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {offering?.title || "Service Details"}
              </Typography>
            </Breadcrumbs>

            {isLoading ? (
              <CircularProgress sx={{ color: "#FFFFFF" }} />
            ) : !offering ? (
              <Typography sx={{ color: "#FFFFFF" }}>
                Service not found.
              </Typography>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
                >
                  <Chip
                    icon={
                      <Wrench size={15} color="#FFFFFF" />
                    }
                    label="IT SERVICES"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.16)",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      "& .MuiChip-icon": { color: "#FFFFFF" },
                    }}
                  />

                  <Chip
                    label="PROFESSIONAL"
                    sx={{
                      bgcolor: "#FFFFFF",
                      color: navy,
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: 32, md: 54 },
                    lineHeight: 1.15,
                    fontWeight: 800,
                    letterSpacing: "-1.2px",
                    mb: 1.5,
                  }}
                >
                  {offering.title}
                </Typography>

                <Typography
                  sx={{
                    color: "#DCE8FF",
                    fontSize: { xs: 15, md: 19 },
                    maxWidth: 800,
                    lineHeight: 2.1,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  {offering.short_description ||
                    "Professional technology services and enterprise solutions."}
                </Typography>
              </>
            )}
          </Container>
        </Box>

        {/* Main content */}
        {!isLoading && offering && (
          <Container
            maxWidth="lg"
            sx={{ mt: { xs: -6, md: -9 }, position: "relative" }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1.8fr) minmax(290px, 0.95fr)",
                },
                gap: { xs: 3, md: 4 },
                alignItems: "start",
              }}
            >
              {/* Left column */}
              <Box sx={{ minWidth: 0 }}>
                {/* Main image */}
                <Paper
                  elevation={0}
                  sx={{
                    height: { xs: 230, sm: 320, md: 390 },
                    borderRadius: "24px",
                    overflow: "hidden",
                    bgcolor: "#EAF0FA",
                    border: "4px solid #FFFFFF",
                    boxShadow: "0 20px 45px rgba(7,27,58,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {offering.icon_or_image ? (
                    <Box
                      component="img"
                      src={offering.icon_or_image}
                      alt={offering.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        p: 2,
                      }}
                    />
                  ) : (
                    <Wrench
                      size={90}
                      color={blue}
                      strokeWidth={1.3}
                    />
                  )}
                </Paper>

                {/* Information cards */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                    mt: 2.5,
                  }}
                >
                  {details.map((item) => {
                    const Icon = item.icon;

                    return (
                      <Paper
                        key={item.label}
                        elevation={0}
                        sx={{
                          p: 2.2,
                          minHeight: 112,
                          borderRadius: "18px",
                          color: "#FFFFFF",
                          background:
                            "linear-gradient(135deg, #17438F 0%, #2864E8 100%)",
                          boxShadow:
                            "0 10px 24px rgba(36,88,211,0.18)",
                        }}
                      >
                        <Icon size={23} />
                        <Typography
                          sx={{
                            mt: 1.5,
                            fontSize: 16,
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {item.value}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#DCE8FF",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>

                {/* Description */}
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: { xs: 2.5, md: 3.5 },
                    borderRadius: "20px",
                    border: "1px solid #E1E8F2",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 24, md: 28 },
                      fontWeight: 800,
                      color: navy,
                      mb: 2.5,
                    }}
                  >
                    About this service
                  </Typography>

                  {offering.short_description && (
                    <Typography
                      sx={{
                        fontSize: 16,
                        color: "#334155",
                        lineHeight: 2.1,
                        mb: 3,
                        textAlign: "justify",
                      }}
                    >
                      {offering.short_description}
                    </Typography>
                  )}

                  {offering.detailed_description && (
                    <>
                      <Typography
                        sx={{
                          fontSize: 21,
                          fontWeight: 700,
                          color: navy,
                          pl: 1.5,
                          borderLeft: "4px solid #2D8CF0",
                          mb: 2,
                        }}
                      >
                        Description
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 16,
                          color: "#334155",
                          lineHeight: 2.1,
                          whiteSpace: "pre-line",
                          textAlign: "justify",
                        }}
                      >
                        {offering.detailed_description}
                      </Typography>
                    </>
                  )}
                </Paper>
              </Box>

              {/* Right column */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  position: { xs: "static", md: "sticky" },
                  top: { md: "110px" },
                  alignSelf: "flex-start",
                  width: "100%",
                }}
              >
                {/* Call to action */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3.2,
                    borderRadius: "24px",
                    color: "#FFFFFF",
                    position: "relative",
                    overflow: "hidden",
                    background:
                      "linear-gradient(145deg, #173F91 0%, #2864E8 100%)",
                    boxShadow: "0 18px 35px rgba(15,55,125,0.24)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: 140,
                      height: 140,
                      borderRadius: "50%",
                      bgcolor: "rgba(255,255,255,0.12)",
                      top: -65,
                      right: -40,
                    },
                  }}
                >
                  <Rocket size={27} />

                  <Typography
                    sx={{
                      mt: 2,
                      fontSize: 24,
                      fontWeight: 800,
                    }}
                  >
                    Ready to start?
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1,
                      mb: 2.5,
                      fontSize: 13,
                      color: "#E2EAFF",
                      lineHeight: 1.8,
                    }}
                  >
                    Contact our team to learn more about this service and
                    discuss your requirements.
                  </Typography>

                  <Button
                    component={Link}
                    to="/business?tab=it-services"
                    fullWidth
                    variant="contained"
                    sx={{
                      py: 1.5,
                      mb: 1.2,
                      borderRadius: "12px",
                      bgcolor: "#FFFFFF",
                      color: navy,
                      fontWeight: 800,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#EAF0FF",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Explore other services
                  </Button>
                </Paper>

                {/* Service details */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "22px",
                    border: "1px solid #E1E8F2",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: navy,
                      mb: 2.5,
                    }}
                  >
                    Service details
                  </Typography>

                  {details.map((item, index) => (
                    <React.Fragment key={item.label}>
                      {index > 0 && <Divider sx={{ my: 1.8 }} />}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 14, color: "#475569" }}
                        >
                          {item.label}
                        </Typography>

                        <Chip
                          size="small"
                          label={item.value}
                          sx={{
                            height: "auto",
                            maxWidth: "65%",
                            bgcolor: "#EAF1FF",
                            color: "#2452A5",
                            fontWeight: 600,
                            "& .MuiChip-label": {
                              display: "block",
                              whiteSpace: "normal",
                              py: 0.7,
                              textAlign: "right",
                              overflowWrap: "anywhere",
                            },
                          }}
                        />
                      </Box>
                    </React.Fragment>
                  ))}

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircle size={19} color="#2458D3" />
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#475569",
                        lineHeight: 1.7,
                      }}
                    >
                      Professional technology solutions for businesses.
                    </Typography>
                  </Stack>
                </Paper>

                {/* Back link */}
                <Button
                  component={Link}
                  to="/business?tab=it-services"
                  startIcon={<ArrowLeft size={17} />}
                  sx={{
                    justifyContent: "flex-start",
                    color: blue,
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  Back to all services
                </Button>
              </Box>
            </Box>
          </Container>
        )}
      </Box>
    </PageShell>
  );
}
